'use client';

import { useQuery } from '@tanstack/react-query';
import {
  type LeadEventAnalyticsDto,
  LeadEventsApi,
} from '@up2stay/up2promo-api-client';
import { Button, Skeleton } from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { useMemo } from 'react';

import {
  LEAD_DETAIL_SECTION_TITLE_CLASS,
  LeadDetailSectionCard,
} from '@/core/components/leads/lead-detail/lead-detail-section-card';
import { leadStatusAccent } from '@/core/components/leads/lead-status-colors';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { useWorkspaceMembersRoster } from '@/core/hooks/use-workspace-members-roster';
import { leadEventsQueryKeys } from '@/core/query-keys/leads-query-keys';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { personInitials } from '@/core/components/leads/leads-list-display';

type LeadEventAnalyticsProps = {
  workspaceId: string;
  eventId: string;
  /** Stacked cards for event-detail right rail (no total hero). */
  layout?: 'page' | 'sidebar';
};

function BreakdownRows({
  rows,
  total,
  barColorForKey,
}: {
  rows: Array<{ key: string; label: string; count: number }>;
  total: number;
  barColorForKey?: (key: string) => string | undefined;
}) {
  if (rows.length === 0) return null;
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <ul className="flex flex-col gap-2.5">
      {rows.map((row) => {
        const pct = total > 0 ? Math.round((row.count / total) * 100) : 0;
        const bar = Math.round((row.count / max) * 100);
        const color = barColorForKey?.(row.key);
        return (
          <li key={row.key} className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between gap-2 text-[13.5px] leading-relaxed">
              <span className="truncate text-foreground">{row.label}</span>
              <span className="shrink-0 text-muted-foreground">
                {row.count}
                {total > 0 ? ` · ${pct}%` : ''}
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className={cn(
                  'h-full rounded-full transition-[width]',
                  !color && 'bg-primary/80',
                )}
                style={{
                  width: `${bar}%`,
                  ...(color ? { backgroundColor: color } : {}),
                }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function LeadEventAnalytics({
  workspaceId,
  eventId,
  layout = 'page',
}: LeadEventAnalyticsProps) {
  const fullDict = useDictionary();
  const dict = fullDict.leads.events.analytics;
  const statusLabels = fullDict.leads.status;
  const sourceLabels = fullDict.leads.list.source;
  const { navigateShell } = useNavigateShell();

  const {
    data: analytics,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: leadEventsQueryKeys.analytics(workspaceId, eventId),
    queryFn: async () => {
      const res = await LeadEventsApi.getEventAnalytics({
        path: { workspaceId, eventId },
      });
      return res.data as LeadEventAnalyticsDto;
    },
    enabled: !!workspaceId && !!eventId,
    staleTime: 30_000,
    retry: false,
  });

  const { data: members = [] } = useWorkspaceMembersRoster(
    workspaceId,
    'event-analytics',
    { staleTime: 60_000 },
  );

  const memberNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of members) {
      const uid = m.userId?.trim();
      if (!uid) continue;
      const u = m.user;
      const name = u
        ? `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || uid
        : uid;
      map.set(uid, name);
    }
    return map;
  }, [members]);

  const sourceRows = useMemo(() => {
    if (!analytics) return [];
    return Object.entries(analytics.bySource)
      .map(([key, count]) => ({
        key,
        label:
          (sourceLabels as Record<string, string>)[key] ??
          (sourceLabels as Record<string, string>)[
            key === 'public_form' ? 'publicForm' : key
          ] ??
          key,
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [analytics, sourceLabels]);

  const statusRows = useMemo(() => {
    if (!analytics) return [];
    return Object.entries(analytics.byStatus)
      .map(([key, count]) => ({
        key,
        label: (statusLabels as Record<string, string>)[key] ?? key,
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [analytics, statusLabels]);

  const memberRows = useMemo(() => {
    if (!analytics) return [];
    return analytics.byRosterMember.map((row) => ({
      key: row.userId,
      label: memberNameById.get(row.userId) ?? row.userId,
      count: row.count,
    }));
  }, [analytics, memberNameById]);

  const statusBarColor = (key: string) => leadStatusAccent(key);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <LeadDetailSectionCard contentClassName="items-center gap-3 text-center">
        <p className="text-[13.5px] leading-relaxed text-muted-foreground">
          {dict.loadError}
        </p>
        <Button variant="outline" onClick={() => void refetch()}>
          {fullDict.leads.loadError.retry}
        </Button>
      </LeadDetailSectionCard>
    );
  }

  if (analytics.totalLeads === 0) {
    return (
      <LeadDetailSectionCard contentClassName="items-center gap-4 text-center">
        <div className="flex flex-col gap-1">
          <h3 className={LEAD_DETAIL_SECTION_TITLE_CLASS}>
            {dict.empty.title}
          </h3>
          <p className="max-w-md text-[13.5px] leading-relaxed text-muted-foreground">
            {dict.empty.description}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => navigateShell(shellPaths.leads)}
        >
          {dict.empty.cta}
        </Button>
      </LeadDetailSectionCard>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-4',
        layout === 'page' ? 'md:gap-5' : 'gap-3',
      )}
    >
      {layout === 'page' ? (
        <LeadDetailSectionCard contentClassName="gap-1">
          <p className="text-[13.5px] leading-relaxed text-muted-foreground">
            {dict.totalLabel}
          </p>
          <p className="text-[26px] font-bold leading-tight tracking-tight text-foreground">
            {analytics.totalLeads}
          </p>
        </LeadDetailSectionCard>
      ) : null}

      <div
        className={cn(
          'grid grid-cols-1 gap-3',
          layout === 'page' && 'gap-4 md:grid-cols-3 md:gap-5',
        )}
      >
        <LeadDetailSectionCard>
          <h3 className={LEAD_DETAIL_SECTION_TITLE_CLASS}>{dict.byStatus}</h3>
          <BreakdownRows
            rows={statusRows}
            total={analytics.totalLeads}
            barColorForKey={statusBarColor}
          />
        </LeadDetailSectionCard>
        <LeadDetailSectionCard>
          <h3 className={LEAD_DETAIL_SECTION_TITLE_CLASS}>{dict.byMember}</h3>
          {memberRows.length === 0 ? (
            <p className="text-[13.5px] leading-relaxed text-muted-foreground">
              {dict.noMemberBreakdown}
            </p>
          ) : (
            <ul className="flex flex-col gap-2.5">
              {memberRows.map((row) => {
                const max = Math.max(...memberRows.map((r) => r.count), 1);
                const bar = Math.round((row.count / max) * 100);
                return (
                  <li key={row.key} className="flex flex-col gap-1">
                    <div className="flex items-center justify-between gap-2 text-[13.5px]">
                      <span className="flex min-w-0 items-center gap-2 truncate">
                        <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-[color:var(--ghost-accent-bg)] text-[9px] font-bold text-[color:var(--ghost-accent-fg)]">
                          {personInitials(row.label)}
                        </span>
                        <span className="truncate">{row.label}</span>
                      </span>
                      <span className="shrink-0 text-muted-foreground">
                        {row.count}
                      </span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-primary/80 transition-[width]"
                        style={{ width: `${bar}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </LeadDetailSectionCard>
        <LeadDetailSectionCard>
          <h3 className={LEAD_DETAIL_SECTION_TITLE_CLASS}>{dict.bySource}</h3>
          <BreakdownRows rows={sourceRows} total={analytics.totalLeads} />
        </LeadDetailSectionCard>
      </div>
    </div>
  );
}
